<script setup>
import { computed, reactive, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  categories: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const form = reactive({ ...props.modelValue });
const matchingAliases = computed({
  get: () => (form.vat_matching_aliases ?? []).join("\n"),
  set: (value) => {
    form.vat_matching_aliases = value.split("\n");
  },
});

watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      return;
    }
    Object.assign(form, value);
  },
  { deep: true },
);

watch(
  form,
  (value) => {
    emit("update:modelValue", { ...value });
  },
  { deep: true },
);
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Company</label
      >
      <input
        type="text"
        v-model="form.company"
        :disabled="readonly"
        class="supplier-input input px-4 py-2"
        required
      />
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Category</label
      >
      <select
        v-model.number="form.category_id"
        :disabled="readonly"
        class="supplier-input input px-4 py-2"
        required
      >
        <option value="" disabled>Select category</option>
        <option
          v-for="category in categories"
          :key="category.id"
          :value="category.id"
        >
          {{ category.name }}
        </option>
      </select>
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >VAT Number</label
      >
      <input
        type="text"
        v-model="form.vat_number"
        :disabled="readonly"
        class="supplier-input input px-4 py-2"
        placeholder="GB123456789"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Default VAT treatment</label
      >
      <select
        v-model="form.default_vat_treatment"
        :disabled="readonly"
        class="supplier-input input px-4 py-2"
      >
        <option value="">Use VAT registration default</option>
        <option value="standard_20">Standard 20%</option>
        <option value="no_vat">No VAT</option>
        <option value="zero_rated">Zero rated</option>
        <option value="exempt">Exempt</option>
        <option value="reverse_charge">Reverse charge</option>
        <option value="needs_review">Needs review</option>
      </select>
    </div>
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Default VAT category</label
      >
      <input
        v-model="form.default_vat_category"
        :disabled="readonly"
        class="supplier-input input px-4 py-2"
      />
    </div>
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Default VAT analysis</label
      >
      <input
        v-model="form.default_vat_analysis"
        :disabled="readonly"
        class="supplier-input input px-4 py-2"
      />
    </div>
    <div class="md:col-span-2 flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Bank transaction matching aliases</label
      >
      <textarea
        v-model="matchingAliases"
        :disabled="readonly"
        rows="3"
        class="supplier-input input px-4 py-2"
        placeholder="One merchant name per line"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400">
        Use distinctive merchant names. VAT matching uses these to associate
        outgoing payments with this supplier.
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Website</label
      >
      <input
        type="text"
        v-model="form.website"
        :disabled="readonly"
        class="supplier-input input px-4 py-2"
        placeholder="https://example.com"
      />
    </div>

    <div class="md:col-span-2 flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300"
        >Address</label
      >
      <textarea
        v-model="form.address"
        :disabled="readonly"
        rows="4"
        class="supplier-input input px-4 py-2"
        placeholder="Street, City, Postcode"
      ></textarea>
    </div>
  </div>
</template>

<style scoped>
.supplier-input {
  @apply w-full rounded-lg border border-slate-300 bg-white text-slate-900 placeholder:text-slate-400 disabled:opacity-60;
  @apply dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100;
  @apply focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/30;
}
select.supplier-input {
  color-scheme: light dark;
}
</style>
